import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ContactFormDialog from "@/components/ContactFormDialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Contact, Plus, Search, Pencil, Trash2, Mail, Phone, Building2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_STYLE = {
  Insurer: "bg-blue-50 text-blue-700",
  Vendor: "bg-amber-50 text-amber-700",
  "Opposing counsel": "bg-rose-50 text-rose-700",
  "Medical provider": "bg-emerald-50 text-emerald-700",
  Expert: "bg-violet-50 text-violet-700",
  Court: "bg-indigo-50 text-indigo-700",
  Other: "bg-gray-100 text-gray-600",
};

export default function Contacts() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [types, setTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (typeFilter) params.type = typeFilter;
    if (search.trim()) params.search = search.trim();
    api.get("/contacts", { params }).then((r) => setContacts(r.data)).catch(() => {});
  }, [search, typeFilter]);

  useEffect(() => { api.get("/contacts/meta").then((r) => setTypes(r.data.types)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const confirmDelete = async () => {
    try { await api.delete(`/contacts/${deleteTarget.id}`); toast.success("Contact deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="contacts-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900 flex items-center gap-2">
            <Contact className="w-7 h-7 text-[#002FA7]" /> Contacts
          </h1>
          <p className="text-sm text-gray-500 mt-1">{contacts.length} contact{contacts.length === 1 ? "" : "s"} · insurers, vendors & more</p>
        </div>
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-contact-button"
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> New contact
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="contact-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, company…"
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-sm w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="contact-type-filter"
          className="text-sm border border-gray-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="">All types</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {contacts.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-16 border border-dashed border-gray-200 rounded-sm" data-testid="contacts-empty">
          No contacts yet. Add insurers, vendors and others to your shared address book.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contacts-list">
          {contacts.map((c) => (
            <div key={c.id} data-testid={`contact-card-${c.id}`}
              className="bg-white border border-gray-200 rounded-sm p-4 shadow-sm hover:border-gray-300 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{c.name}</p>
                  <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full mt-1 ${TYPE_STYLE[c.type] || TYPE_STYLE.Other}`}>{c.type}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`contact-edit-${c.id}`}
                    className="text-gray-400 hover:text-[#002FA7] p-1"><Pencil className="w-4 h-4" /></button>
                  {user?.role === "admin" && (
                    <button onClick={() => setDeleteTarget(c)} data-testid={`contact-delete-${c.id}`}
                      className="text-gray-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                {c.company && <p className="flex items-center gap-2 text-gray-600"><Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" /> <span className="truncate">{c.company}</span></p>}
                {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-[#002FA7] hover:underline"><Mail className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{c.email}</span></a>}
                {c.phone && <p className="flex items-center gap-2 text-gray-600"><Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {c.phone}</p>}
              </div>
              {c.notes && <p className="text-xs text-gray-400 mt-2 line-clamp-2">{c.notes}</p>}
              {c.client_ids?.length > 0 && (
                <p className="text-[11px] text-gray-400 mt-2">Linked to {c.client_ids.length} client{c.client_ids.length === 1 ? "" : "s"}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <ContactFormDialog open={dialogOpen} onOpenChange={setDialogOpen} contact={editing} types={types} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-contact-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete contact?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteTarget?.name}</strong> will be permanently deleted from the address book.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-contact-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
